import React, { useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import { PrimaryButton } from 'src/shared/components/button/Button';
import { HabitatInsightsGeoJsonFile } from 'src/shared/types';
import { useAppSelector } from 'src/store';
import { getCurrentAddSiteToPlatformFormState } from 'src/app/habitat-insights/state/addSiteToPlatformSelector';
import HabitatInsightsSamplesReviewMap from 'src/app/habitat-insights/views/habitat-insights-details/components/habitat-insights-upload/components/review-and-submit/components/habitat-insights-samples-review-map/HabitatInsightsSamplesReviewMap';
import HabitatInsightsSampleSidebar from 'src/app/habitat-insights/views/habitat-insights-details/components/habitat-insights-upload/components/review-and-submit/components/habitat-insights-samples-sidebar/HabitatInsightsSamplesSidebar';
import ErrorMessage from 'src/shared/components/error-msg/ErrorMessage';
import SuccessMessage from 'src/shared/components/success-message/SuccessMessage';
import SnackbarAlert from 'src/shared/components/snackbar-alert/SnackbarAlert';
import styles from './ReviewAndSubmitAddSite.module.scss';
import useAddSiteToPlatform from '../../hooks/useAddSiteToPlatform';

const ReviewAndSubmitAddSite = () => {
    const formState = useAppSelector(getCurrentAddSiteToPlatformFormState);
    const { onFormChange, submitHabitatInsightsUploadForm } = useAddSiteToPlatform();
    const [submissionStatus, setSubmissionStatus] = useState<{
        type: 'success' | 'error' | null;
        message: string;
    }>({ type: null, message: '' });

    const habitatInsightsSamplesData: HabitatInsightsGeoJsonFile = JSON.parse(formState.uploadedFileContent);

    useEffect(() => {
        if (habitatInsightsSamplesData && habitatInsightsSamplesData.features) {
            const newSites = habitatInsightsSamplesData.features;
            if (!isEqual(newSites, formState.sites)) {
                onFormChange({
                    sites: newSites,
                });
            }
        }
    }, [habitatInsightsSamplesData, formState.sites, onFormChange]);

    const onSubmit = async () => {
        setSubmissionStatus({ type: null, message: '' });

        // Prevent submission if there are validation errors
        if (formState.uploadedFileErrors && formState.uploadedFileErrors.length > 0) {
            setSubmissionStatus({
                type: 'error',
                message: 'Please fix validation errors before submitting.',
            });
            return;
        }

        try {
            onFormChange({ loading: true });
            // Skip createProjectInfo for Add Site to Platform - directly submit the upload
            const result = await submitHabitatInsightsUploadForm();

            if (result.success) {
                setSubmissionStatus({
                    type: 'success',
                    message: 'Sites successfully added to platform! Redirecting to admin page...',
                });
            } else {
                setSubmissionStatus({
                    type: 'error',
                    message: result.error || 'Failed to submit sites. Please try again.',
                });
            }
        } catch (e: any) {
            console.error('Submission error:', e);
            setSubmissionStatus({
                type: 'error',
                message: e?.message || 'An unexpected error occurred. Please try again.',
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSubmissionStatus({ type: null, message: '' });
    };

    return (
        <>
            <div className={styles.reviewAndSubmit}>
                <div className={styles.container}>
                    <div className={styles.mainContent}>
                        <HabitatInsightsSamplesReviewMap data={habitatInsightsSamplesData.features} />
                    </div>
                    <div className={styles.sidebar}>
                        <HabitatInsightsSampleSidebar data={habitatInsightsSamplesData.features} />
                    </div>
                </div>
                <div className={styles.bottomNavigation}>
                    <div className={styles.submitSection}>
                        {submissionStatus.type === 'error' && (
                            <div className={styles.statusMessage}>
                                <ErrorMessage message={submissionStatus.message} />
                            </div>
                        )}
                        {submissionStatus.type === 'success' && (
                            <div className={styles.statusMessage}>
                                <SuccessMessage message={submissionStatus.message} />
                            </div>
                        )}
                        <PrimaryButton
                            onClick={onSubmit}
                            disabled={formState.loading || (formState.uploadedFileErrors && formState.uploadedFileErrors.length > 0)}
                            className={styles.submitButton}
                        >
                            {formState.loading ? 'Submitting...' : 'Submit'}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
            <SnackbarAlert
                open={submissionStatus.type === 'success'}
                message={submissionStatus.message}
                severity='success'
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default ReviewAndSubmitAddSite;
